import type {
  AutocompleteSource,
  GetSources,
} from "@algolia/autocomplete-shared";
import { type MouseEventHandler, type ReactNode } from "react";
import { IndexDocument } from "@cappelendamm/catalogue/meilisearch/search-index-receiver.js";
import { getPageLink } from "@/navigation/selectors";
import { autocompleteClient } from "@/meilisearch";
import { DETACHED_MEDIA_QUERY } from "./constants";
import {
  MAX_PRODUCTS,
  PRODUCT_ATTRIBUTES,
  PRODUCT_TYPES,
  groupVariants,
  type SearchProduct,
} from "./products";
import { getMeilisearchResults } from "@meilisearch/autocomplete-client";
import {
  AutocompleteTemplateItem,
  NoResults,
  Portrait,
  ProductCard,
  SearchRow,
  ShowAllButton,
  ShowAllLink,
  SourceHeader,
  Thumbnail,
  TopAuthorCard,
  type AutocompleteTemplateItemProps,
} from "./SourceTemplates";
import {
  ArticleItem,
  BookItem,
  ContributorItem,
  ProductBundleItem,
  SubscriptionItem,
  SeriesItem,
  DigitalServiceItem,
} from "../types";

type HighlightedDocument<Document> = {
  _highlightResult: {
    [K in keyof Document]: Document[K] extends Array<infer U> | undefined
      ? Array<{
          value: U;
          fullyHighlighted: boolean;
          matchLevel: string;
          matchedWords: string[];
        }>
      : {
          value: Document[K];
          fullyHighlighted: boolean;
          matchLevel: string;
          matchedWords: string[];
        };
  };
} & Document;

export type SearchItem = Record<string, unknown>;

export type InitialProducts = {
  title: string;
  items: SearchProduct[];
};

export type QuickLink = {
  label: string;
  href: string;
  external: boolean;
  icon?: ReactNode;
};

const SORTED_INDEX = "items:immediacyScore:desc";
const PRODUCT_HITS = 24;
const DESKTOP_PRODUCTS = 4;

function joinFilters(...filters: Array<string | undefined>) {
  return filters.filter(Boolean).join(" AND ");
}

function formatList(formats: string[] = []) {
  return formats
    .map((format, index) => (index ? format.toLowerCase() : format))
    .join(", ");
}

function getProductHref(item: SearchProduct) {
  if (item.itemType === "book")
    return getPageLink({ pageType: "book", params: item.slug ?? undefined });
  if (item.itemType === "product-bundle")
    return getPageLink({
      pageType: "productBundle",
      params: item.slug ?? undefined,
    });
  if (item.itemType === "subscription")
    return getPageLink({
      pageType: "subscription",
      params: item.slug ?? undefined,
    });
  return `/digitale-tjenester/${item.slug}`;
}

function getProductDetails(item: SearchProduct) {
  if (item.itemType === "book") return formatList(item.formats);
  if (item.itemType === "subscription") return "Abonnement";
  return null;
}

type CatalogueResults = {
  products: SearchProduct[];
  topAuthor?: ContributorItem;
  contributors: ContributorItem[];
  series: SeriesItem[];
  articles: ArticleItem[];
};

type SearchResponse = { results?: Array<{ hits?: unknown[] }> };

function normalize(value: string | null | undefined) {
  return (value ?? "").trim().replace(/\s+/g, " ").toLowerCase();
}

function pickTopAuthor(
  query: string,
  contributors: ContributorItem[],
  products: SearchProduct[],
) {
  const exactMatch = contributors.find(
    ({ name }) => normalize(name) === normalize(query),
  );
  if (exactMatch) return exactMatch;

  const bylines = products.map((product) =>
    product.itemType === "book"
      ? normalize(product.itemDetails?.contributorByLine)
      : "",
  );
  const [best] = contributors
    .map((contributor) => ({
      contributor,
      works: bylines.filter((byline) =>
        byline.includes(normalize(contributor.name)),
      ).length,
    }))
    .filter(({ works }) => works >= 2)
    .sort((a, b) => b.works - a.works);
  return best?.contributor;
}

async function searchCatalogue({
  query,
  filters,
}: {
  query: string;
  filters?: string;
}): Promise<CatalogueResults> {
  const typing = query.trim().length > 0;
  const byType = (type: string, hitsPerPage: number) => ({
    indexName: SORTED_INDEX,
    params: {
      query,
      filters: joinFilters(filters, `itemType = "${type}"`),
      hitsPerPage,
      attributesToRetrieve: ["name", "slug", "imageUrl", "itemType"],
    },
  });

  const requests = [
    {
      indexName: SORTED_INDEX,
      params: {
        query,
        filters: joinFilters(
          filters,
          `itemType IN [${PRODUCT_TYPES.map((type) => `"${type}"`).join(", ")}]`,
        ),
        hitsPerPage: PRODUCT_HITS,
        attributesToRetrieve: PRODUCT_ATTRIBUTES,
      },
    },
    ...(typing
      ? [byType("contributor", 6), byType("series", 3), byType("article", 2)]
      : []),
  ];

  const client = autocompleteClient as unknown as {
    search: (queries: typeof requests) => Promise<SearchResponse>;
  };
  const { results = [] } = await client.search(requests);
  const hits = <T,>(index: number) => (results[index]?.hits ?? []) as T[];

  const products = groupVariants(hits<SearchProduct>(0));
  if (!typing) return { products, contributors: [], series: [], articles: [] };

  const contributors = hits<ContributorItem>(1);
  const topAuthor = pickTopAuthor(query, contributors, products);
  return {
    products,
    topAuthor,
    contributors: contributors
      .filter((contributor) => contributor !== topAuthor)
      .slice(0, topAuthor ? 2 : 3),
    series: hits<SeriesItem>(2),
    articles: hits<ArticleItem>(3),
  };
}

function hasTopAuthor(
  collections: Array<{ source: { sourceId: string }; items: unknown[] }>,
) {
  return collections.some(
    ({ source, items }) => source.sourceId === "topAuthor" && items.length > 0,
  );
}

export function opensAnotherPage(url: string | undefined) {
  if (!url) return false;
  const target = new URL(url, window.location.href);
  return (
    target.origin === window.location.origin &&
    target.pathname + target.search !==
      window.location.pathname + window.location.search
  );
}

export const getSourcesWithFilters =
  ({
    filters,
    quickLinks = [],
    initialProducts,
  }: {
    filters?: string;
    quickLinks?: QuickLink[];
    initialProducts?: InitialProducts;
  }): GetSources<SearchItem> =>
  ({ query, setIsOpen }) => {
    const trimmedQuery = query.trim();
    let catalogue: Promise<CatalogueResults> | undefined;
    const getCatalogue = () =>
      (catalogue ??= searchCatalogue({ query, filters }));
    const closeOnCurrentPage: MouseEventHandler<HTMLAnchorElement> = (
      event,
    ) => {
      if (!opensAnotherPage(event.currentTarget.href)) setIsOpen(false);
    };

    const topAuthor: AutocompleteSource<SearchItem> = {
      sourceId: "topAuthor",
      getItemUrl: ({ item }) =>
        getPageLink({
          pageType: "contributor",
          params: (item as ContributorItem).slug,
        }),
      getItems: async () => {
        if (!trimmedQuery) return [];
        const { topAuthor } = await getCatalogue();
        return topAuthor ? [topAuthor] : [];
      },
      templates: {
        header: () => <SourceHeader>Toppresultat</SourceHeader>,
        item: ({ item }) => {
          const { name, slug, imageUrl } = item as ContributorItem;
          return (
            <TopAuthorCard
              href={getPageLink({ pageType: "contributor", params: slug })}
              name={name ?? ""}
              imageSrc={imageUrl}
            />
          );
        },
      },
    };

    const products: AutocompleteSource<SearchItem> = {
      sourceId: "products",
      getItemUrl: ({ item }) => getProductHref(item as SearchProduct),
      getItems: async () =>
        (!trimmedQuery && initialProducts?.items.length
          ? initialProducts.items
          : (await getCatalogue()).products
        ).slice(
          0,
          window.matchMedia(DETACHED_MEDIA_QUERY).matches
            ? MAX_PRODUCTS
            : DESKTOP_PRODUCTS,
        ),
      templates: {
        header: ({ items, state }) =>
          items.length ? (
            <SourceHeader
              action={
                trimmedQuery ? (
                  <ShowAllLink
                    query={state.query}
                    className="hidden md:flex"
                    onClick={closeOnCurrentPage}
                  />
                ) : null
              }
            >
              {trimmedQuery ? "Bøker" : (initialProducts?.title ?? "Nye bøker")}
            </SourceHeader>
          ) : (
            ""
          ),
        item: ({ item }) => {
          const product = item as SearchProduct;
          return (
            <ProductCard
              href={getProductHref(product)}
              imageSrc={product.imageUrl}
              title={product.name ?? ""}
              byline={
                product.itemType === "book"
                  ? product.itemDetails?.contributorByLine
                  : null
              }
              details={getProductDetails(product)}
            />
          );
        },
        footer: ({ items, state }) =>
          trimmedQuery && items.length ? (
            <ShowAllButton
              query={state.query}
              className="md:hidden"
              onClick={closeOnCurrentPage}
            />
          ) : (
            ""
          ),
        noResults: ({ state }) =>
          trimmedQuery &&
          state.collections.every((collection) => !collection.items.length) ? (
            <NoResults query={state.query} />
          ) : (
            ""
          ),
      },
    };

    const contributors: AutocompleteSource<SearchItem> = {
      sourceId: "contributors",
      getItemUrl: ({ item }) =>
        getPageLink({
          pageType: "contributor",
          params: (item as ContributorItem).slug,
        }),
      getItems: async () =>
        trimmedQuery ? (await getCatalogue()).contributors : [],
      templates: {
        header: ({ state }) => (
          <SourceHeader>
            {hasTopAuthor(state.collections)
              ? "Andre forfattere"
              : "Forfattere"}
          </SourceHeader>
        ),
        item: ({ item }) => {
          const { name, slug, imageUrl } = item as ContributorItem;
          return (
            <SearchRow
              href={getPageLink({ pageType: "contributor", params: slug })}
              label={name ?? ""}
              media={<Portrait name={name ?? ""} imageSrc={imageUrl} />}
            />
          );
        },
      },
    };

    const series: AutocompleteSource<SearchItem> = {
      sourceId: "series",
      getItemUrl: ({ item }) =>
        getPageLink({
          pageType: "bookSeries",
          params: (item as SeriesItem).slug,
        }),
      getItems: async () => (trimmedQuery ? (await getCatalogue()).series : []),
      templates: {
        header: () => <SourceHeader>Serier</SourceHeader>,
        item: ({ item }) => {
          const { name, slug, imageUrl } = item as SeriesItem;
          return (
            <SearchRow
              href={getPageLink({ pageType: "bookSeries", params: slug })}
              label={name ?? ""}
              media={<Thumbnail imageSrc={imageUrl} />}
            />
          );
        },
      },
    };

    const articles: AutocompleteSource<SearchItem> = {
      sourceId: "articles",
      getItemUrl: ({ item }) =>
        getPageLink({
          pageType: "article",
          params: (item as ArticleItem).slug ?? undefined,
        }),
      getItems: async () =>
        trimmedQuery ? (await getCatalogue()).articles : [],
      templates: {
        header: () => <SourceHeader>Artikler</SourceHeader>,
        item: ({ item }) => {
          const { name, slug, imageUrl } = item as ArticleItem;
          return (
            <SearchRow
              href={getPageLink({
                pageType: "article",
                params: slug ?? undefined,
              })}
              label={name ?? ""}
              media={<Thumbnail imageSrc={imageUrl} />}
            />
          );
        },
      },
    };

    const quickLinkSource: AutocompleteSource<SearchItem> = {
      sourceId: "quickLinks",
      getItemUrl: ({ item }) => (item as QuickLink).href,
      getItems: () =>
        quickLinks.filter(
          ({ label }) =>
            !trimmedQuery ||
            label.toLowerCase().includes(trimmedQuery.toLowerCase()),
        ),
      templates: {
        header: () => <SourceHeader>Hurtiglenker</SourceHeader>,
        item: ({ item }) => {
          const { label, href, external, icon } = item as QuickLink;
          return (
            <SearchRow
              href={href}
              external={external}
              label={label}
              media={
                icon ? (
                  <span className="flex size-6 shrink-0 items-center justify-center">
                    {icon}
                  </span>
                ) : null
              }
            />
          );
        },
      },
    };

    return [
      topAuthor,
      products,
      contributors,
      series,
      articles,
      quickLinkSource,
    ].map((source) => ({
      ...source,
      onSelect: ({ item, itemUrl, setIsOpen }) => {
        if ((item as Partial<QuickLink>).external || !opensAnotherPage(itemUrl))
          setIsOpen(false);
      },
    }));
  };

export const getSourcesForEvaluationCopy =
  ({
    onSelect,
  }: {
    onSelect: AutocompleteSource<IndexDocument>["onSelect"];
  }): GetSources<IndexDocument> =>
  ({ query }) => {
    const bookSource: AutocompleteSource<IndexDocument> = {
      sourceId: "items",
      getItemUrl() {
        return "";
      },
      onSelect,
      getItems() {
        return getMeilisearchResults({
          searchClient: autocompleteClient,
          queries: [
            {
              indexName: "items:immediacyScore:desc",
              query,
              params: {
                filters: `(evaluationCopyType EXISTS AND evaluationCopyType IS NOT NULL)`,
                hitsPerPage: 3,
                distinct: query ? undefined : "name",
                attributesToHighlight: ["name", "authors"],
                attributesToRetrieve: [
                  "isbn",
                  "name",
                  "imageUrl",
                  "authors",
                  "language",
                ],
              },
            },
          ],
        }) as unknown as ReturnType<
          AutocompleteSource<IndexDocument>["getItems"]
        >;
      },
      templates: {
        item({ item, components }) {
          const { name, imageUrl, _highlightResult, language } =
            item as HighlightedDocument<BookItem>;
          const props: AutocompleteTemplateItemProps = {
            imageSrc: imageUrl,
            altText: `Produktbilde: ${name ?? "bok"}`,
            highlightName: components.Highlight({
              hit: item,
              attribute: "name",
            }),
            highlightDetails: _highlightResult.authors
              ? components.Highlight({
                  hit: {
                    authors:
                      _highlightResult.authors
                        .map(({ value }) => value)
                        .slice(0, 2)
                        .join(", ") +
                      (_highlightResult.authors.length > 2 ? "…" : ""),
                  },
                  attribute: "authors",
                })
              : undefined,
            itemType: "book",
            language,
          };
          return <AutocompleteTemplateItem {...props} />;
        },
      },
    };
    return [bookSource];
  };
